import React from 'react';
import { DATA_ACT_LOCATIONS, MANUFACTURER_SERVICES_LOCATIONS, TERMS_OF_USE_TABS } from '../config/routerLocations';

export const POSSIBLE_TABS = Object.values(TERMS_OF_USE_TABS)
  .concat(Object.values(MANUFACTURER_SERVICES_LOCATIONS))
  .concat(Object.values(DATA_ACT_LOCATIONS));

interface RoutingContextState {
  isLoaded: boolean;
  activeTab: string;
  setTab: (tab: string) => void;
  replaceTab: (tab: string) => void;
}

interface RoutingContextProviderProps {
}

export const RoutingContext = React.createContext<RoutingContextState>({
  // NOTE: To mitigate setting wrong on initial load, we can use a loading state
  isLoaded: false,
  // NOTE: No default active tab can be set as multiple tab navigation options are possible.
  activeTab: '',
  setTab: () => { },
  replaceTab: () => { },
});

export const RoutingContextProvider: React.FunctionComponent<React.PropsWithChildren<RoutingContextProviderProps>> = ({ children }) => {
  const [activeTab, setActiveTab] = React.useState('');
  const [isLoaded, setIsLoaded] = React.useState(false);

  const getTabFromUrl = React.useCallback(() => {
    const params = new URLSearchParams(window.location.search);
    const tab = params.get('tab') ?? '';
    if (POSSIBLE_TABS.includes(tab)) {
      return tab;
    }
    return null;
  }, []);

  const updateTabInUrl = React.useCallback((tab: string, replace: boolean = false) => {
    const params = new URLSearchParams(window.location.search);
    params.set('tab', tab);
    const newUrl = `${window.location.pathname}?${params.toString()}${window.location.hash}`;

    if (replace) {
      window.history.replaceState({}, '', newUrl);
    } else {
      window.history.pushState({}, '', newUrl);
    }
  }, []);

  const setTab = React.useCallback((tab: string) => {
    setActiveTab(tab);
    updateTabInUrl(tab, false);
  }, [updateTabInUrl]);

  const replaceTab = React.useCallback((tab: string) => {
    setActiveTab(tab);
    updateTabInUrl(tab, true);
  }, [updateTabInUrl]);

  const handleLocationChange = React.useCallback(() => {
    const tab = getTabFromUrl();
    if (tab === null) {
      updateTabInUrl(activeTab, false);
    } else {
      setActiveTab(tab);
    }
  }, [activeTab, getTabFromUrl, updateTabInUrl]);

  React.useEffect(() => {
    handleLocationChange();
    setIsLoaded(true);
  }, []);

  React.useEffect(() => {
    window.addEventListener('popstate', handleLocationChange);
    return () => {
      window.removeEventListener('popstate', handleLocationChange);
    };
  }, [handleLocationChange]);

  const contextValue = React.useMemo(() => ({
    isLoaded,
    activeTab,
    setTab,
    replaceTab,
  }), [isLoaded, activeTab, setTab, replaceTab]);

  return (
    <RoutingContext.Provider value={contextValue}>
      {children}
    </RoutingContext.Provider>
  );
};
